

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    if (parseInt(this.input.value) === 0) {
      this.input.value = this.input.min || 1; // fallback to 1 if no min is defined
    }
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.MENU_TYPE = this.getAttribute('data-menu-type') || new Date().toISOString();
    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.add(`open-drawer-${this.MENU_TYPE}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    document.body.classList.remove(`open-drawer-${this.MENU_TYPE}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus, summaryElement) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    if(summaryElement) summaryElement.setAttribute('aria-expanded', false);
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));

    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });

    if (this.classList.contains('media-modal') && this.tagName === 'PRODUCT-MODAL') {
      this.addEventListener('pointerup', (event) => {
        const clickedImage = event.target.closest('[data-media-id]');
        const clickedCloseBtn = event.target.closest('[id^="ModalClose-"]');

        if (clickedImage || clickedCloseBtn) {
          this.hide();
        } else {
          event.stopPropagation();
        }
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    const stickyAtc = document.querySelector('sticky-add-to-cart');
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);

    if(stickyAtc && stickyAtc.classList.contains('is-visible')){
      document.documentElement.style.setProperty('--gorgias-icon-bottom-offset', '-65px');
      document.documentElement.style.setProperty('--smile-io-icon-bottom-offset', '150px');
    }
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      this.getFormattedSwatchValue(value);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = this.getFormattedSwatchValue(value);

    }
  }


  getFormattedSwatchValue(unformattedValue){
    if (!unformattedValue.includes(':')) return unformattedValue;
    const targetSliceIndex = unformattedValue.indexOf(':');
    const formattedValue = unformattedValue.slice(targetSliceIndex + 1).trim();
    return formattedValue;
    
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }

        const swiperSlider = this.querySelector('swiper-slider');
        if (swiperSlider) {
          swiperSlider.initializeSwiper();
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


// CUSTOM

class Tabs extends HTMLElement {
  constructor() {
    super();
    this.activeTab = null;
    this.activeTabContent = null;
  }

  connectedCallback() {
    this.tabTriggers = this.querySelectorAll('.tab-trigger');
    this.tabContents = this.querySelectorAll('.tab-content');
    this.activeTab = this.tabTriggers[0];
    this.activeTabContent = this.tabContents[0];
    this.attachEvents();
    this.checkUrlHash();
  }

  attachEvents() {
    this.tabTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.toggleTabs(trigger);
      });
    });
  }

  toggleTabs(tab) {
    console.log('tab', tab);
    if (this.activeTab) {
      this.activeTab.removeAttribute('active');
      this.activeTabContent.removeAttribute('open');
      if (this.activeTab.closest('.tabs-triggers__trigger'))
        this.activeTab.closest('.tabs-triggers__trigger').removeAttribute('tab-trigger-active');
    }

    this.activeTab = tab;
    this.activeTabContent = document.getElementById(this.activeTab.dataset.target);
    console.log('this.activeTab', this.activeTab);
    console.log('this.activeTabContent', this.activeTabContent);

    if (this.activeTab.closest('.tabs-triggers__trigger'))
      this.activeTab.closest('.tabs-triggers__trigger').setAttribute('tab-trigger-active', '');

    this.activeTab.setAttribute('active', '');
    this.activeTabContent.setAttribute('open', '');
  }

  checkUrlHash() {
    const hash = window.location.hash.substring(1); // Remove the '#' symbol
    if (!hash) return;

    const matchingTrigger = Array.from(this.tabTriggers).find(
      trigger => trigger.dataset.handle === hash
    );

    if (matchingTrigger) {
      this.toggleTabs(matchingTrigger);
    }
  }
}

customElements.define('site-tabs', Tabs);



class AccordionRow extends HTMLElement {
  constructor() {
    super();

    this.details = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.querySelector('.accordion__content');

    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;

    if(this.summary){
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }
  }

  onClick(e) {
    e.preventDefault();

    this.details.style.overflow = 'hidden';
    if (this.isClosing || !this.details.open) {
      this.open();
    }
    else if (this.isExpanding || this.details.open) {
      this.shrink();
    }
  }

  shrink() {
    this.isClosing = true;
    
    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop)}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 250,
      easing: 'ease'
    });
    
    this.animation.onfinish = () => this.onAnimationFinish(false);
    this.animation.oncancel = () => this.isClosing = false;
  }

  open() {
    this.details.style.height = `${this.details.offsetHeight}px`;
    this.details.open = true;
    window.requestAnimationFrame(() => this.expand());
  }

  expand() {
    this.isExpanding = true;

    const summaryStyle = window.getComputedStyle(this.summary);
    const startHeight = `${this.details.offsetHeight}px`;
    const endHeight = `${this.summary.offsetHeight + parseInt(summaryStyle.marginTop) + this.content.offsetHeight}px`;
    
    if (this.animation) {
      this.animation.cancel();
    }
    
    this.animation = this.details.animate({
      height: [startHeight, endHeight]
    }, {
      duration: 400,
      easing: 'ease-out'
    });
    this.animation.onfinish = () => this.onAnimationFinish(true);
    this.animation.oncancel = () => this.isExpanding = false;
  }

  onAnimationFinish(open) {
    this.details.open = open;
    this.animation = null;
    this.isClosing = false;
    this.isExpanding = false;
    this.details.style.height = this.details.style.overflow = '';
  }
}

customElements.define('accordion-row', AccordionRow);


const accordionRow = document.createElement('accordion-row');
document.body.appendChild(accordionRow); 

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.activeLink = null;
    this.activeMenu = null;
    this.menuInner = this.querySelector('.mega-menu__inner');
    this.header = document.querySelector('.section-header');
    this.mainSearch = null;
    this.links = this.header.querySelectorAll('.header__menu-item');
    this.menuContainers = Array.from(document.querySelectorAll('.mega-menu-container'));
    this.menus = Array.from(document.querySelectorAll('mega-menu'));
    
    this.openMenu = this.openMenu.bind(this);
    this.closeMenu = this.closeMenu.bind(this);
  }

  connectedCallback() {
    this.setTargetLinksAsMegaLinks();
    this.megaMenuLinks = Array.from(this.header.querySelectorAll('.mega-menu-link'));

    this.megaMenuLinks.forEach((link) => {
        if (link != this.activeLink) link.addEventListener('mouseenter', () => this.closeMenu());
        link.addEventListener('mouseenter', () => this.openMenu(link.dataset.menu));
    });

    this.links.forEach((link) => {
        link.addEventListener('mouseenter', () => {
            if (link !== this.activeLink) {
                this.closeMenu();
            }
        });
    });

    this.menuInner.addEventListener('mouseleave', (e) => {
        this.closeMenu();
    });


    this.header.addEventListener('mouseleave', (e) => {
      let target = e.relatedTarget;
      if(e.relatedTarget && e.relatedTarget.classList.contains('mega-menu--submenu')){
         this.closeMenu();
      }
    });

    // let closeMenuTimeout;
    // this.header.addEventListener('mouseleave', (e) => {
    //   console.log(e);
    
    //   if (e.relatedTarget && e.relatedTarget.closest('.mega-menu--submenu')) {
    //     // Do nothing if the mouse is moving into a submenu
    //     return;
    //   }
    
    //   // Otherwise, close the menu after a delay
    //   closeMenuTimeout = setTimeout(() => {
    //     this.closeMenu();
    //   }, 500);
    // });
    
    // // Clear timeout if user moves back into the menu
    // this.menuInner.addEventListener('mouseenter', () => {
    //   clearTimeout(closeMenuTimeout);
    // });


    document.addEventListener('click', (e) => {
        if(!e.target.closest('.mega-menu__inner')){
         this.closeMenu(); 
        }
    });

    // document.addEventListener('DOMContentLoaded', () => {
    //     this.decorateMenuLinks();
    // });

    // ADMIN EVENT LISTENERS
    if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', (e) => {
            const targetSection = e.target.closest('.shopify-section');
            const targetMegamenu = targetSection.querySelector('mega-menu');
            if (targetMegamenu) this.openMenu(targetMegamenu.id);
        });

        document.addEventListener('shopify:section:select', (e) => {
            const targetMegamenu = e.target.querySelector('mega-menu');
            if (targetMegamenu) this.openMenu(targetMegamenu.id);
        });

        document.addEventListener('shopify:section:deselect', (e) => {
            this.closeMenu();
        });
    }
}

  openMenu(targetId) {      
    if (!targetId) return; 
    const menu = document.getElementById(targetId);
    const link = document.querySelector(`[data-menu=${targetId}]`);

    if(this.classList.contains('mega-menu--submenu')){
      this.setAttribute("style", `--left-offset:${link.offsetLeft}px`);
    }

    if(this.activeMenu && this.activeMenu != menu){
      this.closeMenu;
    } else {
    this.activeMenu = menu;
    this.activeLink = link;
    if (this.activeMenu != null) {
      this.activeLink.setAttribute('open', '');
      this.activeMenu.setAttribute('open', '');
      menu.focus();
    }
    }
  }

  setTargetLinksAsMegaLinks() {
    this.links.forEach(link => {
      if(link.dataset.menu == this.id) link.classList.add('mega-menu-link');
    });
  }

  decorateMenuLinks() {
    this.megaMenuLinks.forEach((megaLink) => {
      if (!megaLink.querySelector('.icon')) {
        const megaMenuIcon = document.createElement("div");
        megaMenuIcon.classList.add('icon');
        megaMenuIcon.innerHTML = ` 
          <svg style="width: 1.15rem;height: 1.15rem;" viewBox="0 0 10 6">
            <path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"></path>
          </svg>`
        megaLink.appendChild(megaMenuIcon);
      }
    });
  }

  closeMenu() {
    if (this.activeMenu) {
      this.activeMenu.removeAttribute('open');
      this.activeLink.removeAttribute('open');
      this.activeMenu = null;
      this.activeLink = null;
      return;
    }
  }

  toggleYScroll() {
      document.body.classList.contains('scroll-blocked') 
          ? document.body.classList.remove('scroll-blocked') 
          : document.body.classList.add('scroll-blocked');
  }
}

customElements.define('mega-menu', MegaMenu);

class StickyStateTracker {
  constructor() {
      this.elementsMap = new Map();
      this.header = document.querySelector('.section-header');
      this.headerHeightValue = this.header ? this.header.offsetHeight : 0;
      this.lastScrollY = window.scrollY;
      this.init();
  }

  init() {
      this.observeDOMChanges();
      this.addStickyObservers();
      this.setupListeners();
  }

  setupListeners() {
      // Use a debounced or throttled scroll handler
      this.handleScroll = this.throttle(this.handleScroll.bind(this), 50);
      window.addEventListener('scroll', this.handleScroll);

      // Update header height on resize
      window.addEventListener('resize', this.updateHeaderHeight.bind(this));
  }

  throttle(callback, limit) {
      let wait = false;
      return function (...args) {
          if (!wait) {
              callback.apply(this, args);
              wait = true;
              setTimeout(() => (wait = false), limit);
          }
      };
  }

  updateHeaderHeight() {
      this.headerHeightValue = this.header ? this.header.offsetHeight : 0;
  }

  addStickyObservers() {
      const stickyElements = document.querySelectorAll('.js-track-sticky-state');
      stickyElements.forEach((element) => {
          if (!this.elementsMap.has(element)) {
              const elementOffsetTop = element.offsetTop;
              this.elementsMap.set(element, { isSticky: false, offsetTop: elementOffsetTop });
          }
      });
  }

  handleScroll() {
      const currentScrollY = window.scrollY;
      this.elementsMap.forEach((state, element) => {
          const isSticky = currentScrollY + this.headerHeightValue >= state.offsetTop;

          if (isSticky && !state.isSticky) {
              element.setAttribute('is-sticky', 'true');
              state.isSticky = true;
              console.log('sticky')
            
          } else if (!isSticky && state.isSticky) {
              element.removeAttribute('is-sticky');
              state.isSticky = false;
              console.log('not sticky')
          }
      });
  }

  observeDOMChanges() {
      const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
              if (mutation.type === 'childList') {
                  mutation.addedNodes.forEach((node) => {
                      if (node.nodeType === 1 && node.matches('.js-track-sticky-state')) {
                          this.elementsMap.set(node, { isSticky: false, offsetTop: node.offsetTop });
                      }
                  });

                  mutation.removedNodes.forEach((node) => {
                      if (node.nodeType === 1 && this.elementsMap.has(node)) {
                          this.elementsMap.delete(node);
                      }
                  });
              }
          });
      });

      observer.observe(document.body, { childList: true, subtree: true });
  }

  disconnect() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.updateHeaderHeight);
      this.elementsMap.clear();
  }
}

const stickyTracker = new StickyStateTracker();


class ShopifyFormatter {
  constructor(moneyFormat = "{{ amount }}") {
    this.moneyFormat = moneyFormat;
  }

  formatMoney(cents, format) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const formatString = format || this.moneyFormat;
    const placeholderRegex = /{{\s*(\w+)\s*}}/;

    const formatWithDelimiters = (number, precision = 2, thousands = ",", decimal = ".") => {
      if (isNaN(number) || number == null) return "0";

      const formattedNumber = (number / 100).toFixed(precision);
      const [dollars, centsPart] = formattedNumber.split(".");

      const dollarsFormatted = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      const centsFormatted = centsPart ? `${decimal}${centsPart}` : "";

      return `${dollarsFormatted}${centsFormatted}`;
    };

    const formatOptions = {
      amount: () => formatWithDelimiters(cents, 2),
      amount_no_decimals: () => formatWithDelimiters(cents, 0),
      amount_with_comma_separator: () => formatWithDelimiters(cents, 2, ".", ","),
      amount_no_decimals_with_comma_separator: () => formatWithDelimiters(cents, 0, ".", ","),
    };

    const formatKey = formatString.match(placeholderRegex)?.[1];
    const value = formatKey && formatOptions[formatKey]?.() || "0";

    return formatString.replace(placeholderRegex, value);
  }
}

// Usage Example
const shopifyFormatter = new ShopifyFormatter("{{ amount_with_comma_separator }}");

// class ColorOptionPreviewer extends HTMLElement {
//   constructor() {
//     super();
//     this.init();
//   }

//   init() {
//     const swatches = this.querySelectorAll(".js-swatch-button");
//     swatches.forEach((swatch) => {
//       swatch.addEventListener("click", (event) => this.handleSwatchClick(event, swatch, swatches));
//     });
//   }

//   handleSwatchClick(event, swatch, swatches) {
//     const primarySrc = swatch.getAttribute("data-primary-src");
//     const primarySrcSet = swatch.getAttribute("data-primary-srcset");
//     const secondarySrc = swatch.getAttribute("data-secondary-src");
//     const secondarySrcSet = swatch.getAttribute("data-secondary-srcset");
//     const selectedSwatchButton = event.target;

//     const productItem = swatch.closest(".product-card-wrapper");
//     if (!productItem) return;

//     const primaryImage = productItem.querySelector(".card__primary-picture");
//     const secondaryImage = productItem.querySelector(".card__secondary-picture");

//     swatches.forEach(swatch => {swatch.classList.remove('checked')});
//     swatch.classList.add('checked');
    

//     if (primaryImage) {
//       this.animateImageChange(primaryImage, primarySrc, this.getImageSrcSet(primarySrcSet));
//     }

//     if (secondaryImage) {
//       this.changeImageSource(secondaryImage, secondarySrc, this.getImageSrcSet(secondarySrcSet));
//     }

//   }



//   changeImageSource(imageElement, newSrc, newSrcSet) {
//     imageElement.src = newSrc;
//     imageElement.srcset = newSrcSet;
//   }

//   animateImageChange(imageElement, newSrc, newSrcSet) {
//     imageElement.style.transition = "opacity .350s ease-in-out, visibility .350s ease-in-out";
//     imageElement.style.opacity = "0";
    
//     const onTransitionEnd = () => {
//       imageElement.style.removeProperty("opacity");
//       imageElement.style.removeProperty("visibility");
      
//       imageElement.removeEventListener('transitionend', onTransitionEnd);
//     };
  
//     imageElement.addEventListener('transitionend', onTransitionEnd);
  
//     setTimeout(() => {
//       // Update image sources after the fade-out effect starts
//       this.changeImageSource(imageElement, newSrc, newSrcSet);
  
//       // Force reflow to ensure the browser registers the source change before animating
//       void imageElement.offsetHeight;
  
//       // Fade the image back in
//       imageElement.style.opacity = "1";
//       imageElement.style.visibility = "visible";
//     }, 350); // Matching the transition duration
//   }

//   getImageSrcSet(src) {
//     return `
//       ${src}&width=200 200w,
//       ${src}&width=300 300w, 
//       ${src}&width=400 400w,
//       ${src}&width=500 500w,
//       ${src}&width=600 600w,
//       ${src}&width=700 700w,
//       ${src}&width=800 800w,
//       ${src}&width=900 900w,
//       ${src}&width=1000 1000w,
//       ${src}&width=1100 1100w,
//       ${src}&width=1200 1200w
//     `;
//   }
// }

// customElements.define("color-option-previewer", ColorOptionPreviewer);


class ColorOptionPreviewer extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    const swatches = Array.from(this.querySelectorAll('.js-swatch-button'));

    // Precompute srcsets
    this.precomputedSrcSets = new Map();
    swatches.forEach((swatch) => {
      const basePrimary = swatch.dataset.primarySrc;
      const baseSecondary = swatch.dataset.secondarySrc;
      this.precomputedSrcSets.set(basePrimary, this.getImageSrcSet(basePrimary));
      this.precomputedSrcSets.set(baseSecondary, this.getImageSrcSet(baseSecondary));
    });

    swatches.forEach((swatch) => {
      swatch.addEventListener(
        'click',
        this.debounce((event) => this.handleSwatchClick(event, swatch, swatches), 50),
        { passive: true }
      );
    });
  }

  handleSwatchClick(event, swatch, swatches) {
    const baseSrcPrimary = swatch.dataset.primarySrc;
    const baseSrcSecondary = swatch.dataset.secondarySrc;
    const targetUrl = swatch.dataset.targetUrl;

    const productItem = swatch.closest('.product-card-wrapper');
    if (!productItem) return;

    const primaryImage = productItem.querySelector('.card__primary-picture');
    const secondaryImage = productItem.querySelector('.card__secondary-picture');
    const productItemLinks = productItem.querySelectorAll('a');

    // Update styles efficiently
    requestAnimationFrame(() => {
      swatches.forEach((item) => item.classList.remove('checked'));
      swatch.classList.add('checked');

      if (primaryImage) {
        this.updateImageWithTransition(
          primaryImage,
          this.getImageSrc(baseSrcPrimary),
          this.precomputedSrcSets.get(baseSrcPrimary),
          1
        );
      }

      if (secondaryImage) {
        this.updateImageWithTransition(
          secondaryImage,
          this.getImageSrc(baseSrcSecondary),
          this.precomputedSrcSets.get(baseSrcSecondary),
          0
        );
      }

      productItemLinks.forEach((link) => {
        if (targetUrl) {
          link.href = targetUrl;
        }
      });
    });
  }

  updateImageWithTransition(imageElement, newSrc, newSrcSet, finalOpacity) {
    if (imageElement.src !== newSrc) {
      imageElement.src = newSrc;
      imageElement.srcset = newSrcSet;
    }
  }

  getImageSrc(baseSrc) {
    return `${baseSrc}&width=750 750w`;
  }

  getImageSrcSet(baseSrc) {
    return Array.from({ length: 4 }, (_, i) => {
      const size = (i + 4) * 100;
      return `${baseSrc}&width=${size} ${size}w`;
    }).join(', ');
  }

  debounce(func, wait) {
    let timeout;
    return function (...args) {
      const later = () => {
        clearTimeout(timeout);
        func.apply(this, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

customElements.define('color-option-previewer', ColorOptionPreviewer);



if (!customElements.get("marquee-slider")) {
class MarqueeSlider extends HTMLElement {
    constructor() {
        super();
        if (window.ResizeObserver) {
        new ResizeObserver(entries => this._calculateDuration(entries)).observe(this);
        }
    }

    _calculateDuration(entries) {
        const scrollingSpeed = parseInt(this.getAttribute("scrolling-speed") || 5);
        const contentWidth = entries[0].contentRect.width;
        const slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);

        this.style.setProperty(
        "--marquee-animation-duration",
        `${(scrollingSpeed * slowFactor * entries[0].target.querySelector(".scrolling-text__container").clientWidth / contentWidth).toFixed(3)}s`
        );
    }
    }

    if (!window.customElements.get("marquee-slider")) {
    window.customElements.define("marquee-slider", MarqueeSlider);
    }
}

class StickyAddToCart extends HTMLElement {
  #scopeFromPassed = false;
  #scopeToReached = false;
  #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));

  connectedCallback() {
    this._scopeFrom = document.getElementById(this.getAttribute("form"));
    this._scopeTo = document.querySelector(".footer");

    if (!this._scopeFrom || !this._scopeTo) {
      return;
    }

    this.#intersectionObserver.observe(this._scopeFrom);
    this.#intersectionObserver.observe(this._scopeTo);

    // Check scroll position on load
    window.addEventListener("load", this._checkInitialScroll.bind(this));
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    window.removeEventListener("load", this._checkInitialScroll.bind(this));
  }

  _onObserved(entries) {
      if (window.matchMedia("(min-width: 990px)").matches) {
    return;
  }
    entries.forEach((entry) => {
      if (entry.target === this._scopeFrom) {
        this.#scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this._scopeTo) {
        this.#scopeToReached = entry.isIntersecting;
      }
    });
  
    // Toggle visibility class based on condition
    this.classList.toggle("is-visible", this.#scopeFromPassed && !this.#scopeToReached);
  
    // Set the --gorgias-icon-bottom-offset based on visibility
    if (this.#scopeFromPassed && !this.#scopeToReached) {
      document.documentElement.style.setProperty('--gorgias-icon-bottom-offset', '-65px');
      document.documentElement.style.setProperty('--smile-io-icon-bottom-offset', '150px');
    } else {
      document.documentElement.style.setProperty('--gorgias-icon-bottom-offset', '0px');
      document.documentElement.style.setProperty('--smile-io-icon-bottom-offset', '80px');
    }
  }  

  _checkInitialScroll() {
    if (!this._scopeFrom) return;
    const scrolledPastForm = this._scopeFrom.getBoundingClientRect().bottom < 0;
    console.log("Is scroll past form on load:", scrolledPastForm);
  }
}

if (!window.customElements.get("sticky-add-to-cart")) {
  window.customElements.define("sticky-add-to-cart", StickyAddToCart);
}




const links = document.querySelectorAll('a');

links.forEach(link => {
    link.addEventListener('click', function(event) {
        if (link.href.includes('#smile')) {
            const summaryEl = document.querySelector('[aria-controls="menu-drawer"]');
            closeDrawer('header-drawer', event, summaryEl);
        }
    });
});

function closeDrawer(targetDrawer, event, summaryEl) {
  const drawer = document.querySelector(targetDrawer);
  drawer.closeMenuDrawer(event, document.body, summaryEl);
}


document.addEventListener("DOMContentLoaded", function () {

    const footer = document.querySelector(".footer");

    if (!footer) {
        return;
    }

    // Select elements dynamically by their CSS selectors
    const elementsToHideSelectors = [
        "#smile-lite-launcher-frame", 
        ".cky-btn-revisit-wrapper", 
        "#gorgias-chat-container"
    ];

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach(entry => {
                console.log("Intersection Ratio:", entry.intersectionRatio);

                // Only trigger action if the footer is 20% visible or more
                if (entry.intersectionRatio > 0.2) { 
                    console.log("Hiding third-party elements...");

                    // Dynamically select elements based on the CSS selectors
                    elementsToHideSelectors.forEach(selector => {
                        const el = document.querySelector(selector);
                        if (el) {
                            el.classList.add("hidden");
                        } else {
                        }
                    });
                } else {
                    console.log("Showing elements...");

                    // Show the elements again when the footer is less than 20% visible
                    elementsToHideSelectors.forEach(selector => {
                        const el = document.querySelector(selector);
                        if (el) {
                            el.classList.remove("hidden");
                        }
                    });
                }
            });
        },
        { threshold: [0.2] } // Trigger when 20% of the footer is visible
    );

    observer.observe(footer);
});

class Tooltip {
  constructor() {
    this.tooltip = null;
    this.enabled = window.innerWidth > 750;
    this.bound = false;

    this._onResize = this._onResize.bind(this);
    window.addEventListener('resize', this._onResize);

    if (this.enabled) {
      this.bindEvents();
    }
  }

  _onResize() {
    const shouldEnable = window.innerWidth > 750;
    if (shouldEnable && !this.bound) {
      this.bindEvents();
      this.enabled = true;
    } else if (!shouldEnable && this.bound) {
      this.unbindEvents();
      this.enabled = false;
      this.hideTooltip();
    }
  }

  bindEvents() {
    this.targets = document.querySelectorAll('[js-tooltip-el]');
    this.targets.forEach(el => {
      el.addEventListener('mouseenter', this._onEnter = e => this.showTooltip(e.currentTarget));
      el.addEventListener('mouseleave', this._onLeave = () => this.hideTooltip());
      el.addEventListener('mousemove', this._onMove = e => this.moveTooltip(e));
    });
    this.bound = true;
  }

  unbindEvents() {
    if (!this.targets) return;
    this.targets.forEach(el => {
      el.removeEventListener('mouseenter', this._onEnter);
      el.removeEventListener('mouseleave', this._onLeave);
      el.removeEventListener('mousemove', this._onMove);
    });
    this.bound = false;
  }

  createTooltip(content) {
    const tooltip = document.createElement('div');
    tooltip.classList.add('js-tooltip', 'tooltip-el');
    tooltip.textContent = content;

    // minimal styles
    tooltip.style.position = 'fixed';
    tooltip.style.top = '-10px';
    tooltip.style.left = '0';
    tooltip.style.transform = 'translate(-50%, -100%)';
    tooltip.style.pointerEvents = 'none';

    document.body.appendChild(tooltip);
    return tooltip;
  }

  showTooltip(el) {
    if (!this.enabled) return;
    const content = el.getAttribute('tooltip-content');
    if (!content) return;
    if (this.tooltip) this.tooltip.remove();
    this.tooltip = this.createTooltip(content);
  }

  moveTooltip(e) {
    if (!this.tooltip || !this.enabled) return;
    this.tooltip.style.top = `${e.clientY - 8}px`;
    this.tooltip.style.left = `${e.clientX}px`;
  }

  hideTooltip() {
    if (this.tooltip) {
      this.tooltip.remove();
      this.tooltip = null;
    }
  }
}

document.addEventListener('DOMContentLoaded', () => new Tooltip());


/* adding local currency as an available option for the currency selector */
function initLocalCurrencyPicker() {
  if (!window.countryCurrencyMap) return;

  fetch('/browsing_context_suggestions.json')
    .then(function(res) { return res.json(); })
    .then(function(data) {
      var detectedCountry = data.detected_values.country.handle;
      var match = window.countryCurrencyMap[detectedCountry];
      if (!match) return;

      document.querySelectorAll('.disclosure__list').forEach(function(list) {
        var alreadyExists = false;
        list.querySelectorAll('.localization-form__currency').forEach(function(el) {
          if (el.textContent.trim().indexOf(match.currency) === 0) {
            alreadyExists = true;
          }
        });

        if (!alreadyExists) {
          var localItem = list.querySelector('.disclosure__item--local');
          if (localItem) {
            localItem.querySelector('.disclosure__link').setAttribute('data-value', detectedCountry);
            localItem.querySelector('.localization-form__currency').textContent = match.currency + ' ' + match.symbol;
            localItem.style.display = '';
          }
        }
      });
    })
    .catch(function(err) {
      console.log('Geolocation fetch failed:', err);
    });
}

initLocalCurrencyPicker();